import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import axios from "../../utils/api.js";

const api = axios.create({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  country: "",
  zipCode: "",
};

function DistributorApp() {
  const [distributors, setDistributors] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      const response = await api.get("/distributors");
      setDistributors(response.data);
    } catch (error) {
      console.error("Error fetching distributors:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/update-distributor/${editingId}`, formData);
      } else {
        await api.post("/distributor", formData);
      }
      setFormData(emptyForm);
      setEditingId(null);
      setShowPopup(false);
      fetchDistributors();
    } catch (error) {
      console.error("Error saving distributor:", error);
    }
  };

  const openAddPopup = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowPopup(true);
  };

  const handleEdit = (distributor) => {
    setEditingId(distributor._id);
    setFormData({ ...emptyForm, ...distributor });
    setShowPopup(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this distributor?")) return;
    try {
      await api.delete(`/delete-distributor/${id}`);
      fetchDistributors();
    } catch (error) {
      console.error("Error deleting distributor:", error);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.5rem 0.65rem",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
    marginBottom: 8,
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const popupStyle = {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 14,
    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    width: "100%",
    maxWidth: 380,
    maxHeight: "85vh",
    overflowY: "auto",
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        padding: "24px 32px 48px",
        boxSizing: "border-box",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <button
        onClick={openAddPopup}
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          padding: "0.5rem 1.25rem",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
          zIndex: 10,
        }}
      >
        Add Distributor
      </button>

      <div style={{ textAlign: "center", marginTop: 56, marginBottom: 32 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Distributor Management
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Add, edit, and manage distributors
        </p>
      </div>

      {/* Popup: Add / Update Distributor */}
      {showPopup && (
        <div style={overlayStyle} onClick={closePopup}>
          <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
            <h3
              style={{
                margin: "0 0 14px",
                color: "#1e293b",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              {editingId ? "Update Distributor" : "Add Distributor"}
            </h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
              <input
                type="text"
                name="phone"
                placeholder="Phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
              <input
                type="text"
                name="address"
                placeholder="Address"
                value={formData.address}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 72px", gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
                <input
                  type="text"
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
                <input
                  type="text"
                  name="zipCode"
                  placeholder="Zip"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
              </div>
              <input
                type="text"
                name="country"
                placeholder="Country"
                value={formData.country}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: "0.5rem 0.75rem",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {editingId ? "Update" : "Add"} Distributor
                </button>
                <button
                  type="button"
                  onClick={closePopup}
                  style={{
                    padding: "0.5rem 0.75rem",
                    backgroundColor: "#f1f5f9",
                    color: "#64748b",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Distributor List */}
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <h3
          style={{
            margin: "0 0 16px",
            fontSize: "1.1rem",
            fontWeight: 600,
            color: "#1e293b",
          }}
        >
          Distributor List
        </h3>
        {distributors.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>
            No distributors yet. Click "Add Distributor" to create one.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {distributors.map((distributor) => (
              <div
                key={distributor._id}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 16,
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div style={{ flex: "1 1 200px" }}>
                  <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>
                    {distributor.name}
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {distributor.email} · {distributor.phone}
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                    {distributor.address}, {distributor.city}, {distributor.state},{" "}
                    {distributor.country} - {distributor.zipCode}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => handleEdit(distributor)}
                    style={{
                      padding: "6px 14px",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(distributor._id)}
                    style={{
                      padding: "6px 14px",
                      backgroundColor: "#dc2626",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DistributorApp;
